import { isValidElement, ReactNode, useState } from 'react';

import { Box, IconButton, Menu, MenuItem, useTheme } from '@mui/material';
import {
  AdvancedMarker,
  AdvancedMarkerProps,
  InfoWindow,
  useAdvancedMarkerRef,
  useMap,
} from '@vis.gl/react-google-maps';
import { differenceInMinutes } from 'date-fns';

import { IcClose } from '@/assets/images';
import { DroppableIcMarkerCfs } from '@/components/DragAndDrop/DroppableIcMarkerCfs';
import { PointLayerType, IHydrantProperties } from '@/models';

import FireHydrantPopup from './FireHydrantPopup';
export interface IMarker extends Omit<AdvancedMarkerProps, 'position'> {
  key: string;
  position: google.maps.LatLngLiteral;
  icon?: ReactNode | string;
  info?: string | ReactNode;
  className?: string;
  isDraggable?: boolean;
  isDroppable?: boolean;
  cfsId?: string;
  cfsNumber?: string;
  address?: string;
  active?: boolean;
  properties?: Record<string, any>;
  isImported?: boolean;
  createdBy?: string;
  status?: string;
  type?: PointLayerType | string;
  grayedOut?: boolean;
  isExpired?: boolean;
  updatedAt?: string;
  /** When true, the info window content is wrapped with a full-bleed theme background (e.g. for dark mode). */
  infoWindowFullBg?: boolean;
}

interface MapMarkerProps extends IMarker {
  showInfo: boolean;
  maxWidth?: number;
  defaultInfoOpen?: boolean;
  toggleInfo: () => void;
  showContextMenu?: boolean;
  className?: string;
}

const ContextMenu = ({
  anchorEl,
  open,
  handleMenuClose,
  toggleStreetView,
  onClick,
  selectedEvent,
  isStreetViewAvailable,
}: {
  anchorEl: HTMLElement | null;
  open: boolean;
  handleMenuClose: () => void;
  toggleStreetView: () => void;
  onClick?: (event: google.maps.MapMouseEvent) => void;
  selectedEvent: google.maps.MapMouseEvent;
  isStreetViewAvailable?: boolean;
}) => (
  <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
    <MenuItem
      disabled={!isStreetViewAvailable}
      onClick={() => {
        toggleStreetView();
        handleMenuClose();
      }}
    >
      Street View
    </MenuItem>
    <MenuItem
      onClick={() => {
        if (onClick) {
          onClick(selectedEvent);
        }
        handleMenuClose();
      }}
    >
      CFS Details
    </MenuItem>
  </Menu>
);

export const MapMarker = ({
  icon,
  info,
  showInfo,
  defaultInfoOpen,
  maxWidth,
  toggleInfo,
  onClick,
  showContextMenu,
  className,
  isDroppable,
  cfsId,
  cfsNumber,
  address,
  grayedOut,
  properties,
  infoWindowFullBg,
  ...rest
}: MapMarkerProps) => {
  const theme = useTheme();
  const [markerRef, marker] = useAdvancedMarkerRef();
  const map = useMap();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEvent, setSelectedEvent] =
    useState<google.maps.MapMouseEvent | null>(null);
  const [isStreetViewAvailable, setIsStreetViewAvailable] = useState(false);
  const [interactionType, setInteractionType] = useState<
    'hover' | 'click' | null
  >(null);

  const open = Boolean(anchorEl);

  if (!map) return null;

  const handleMenuClose = () => setAnchorEl(null);

  const streetView = map.getStreetView();

  const handleMenuClick = (event: google.maps.MapMouseEvent) => {
    setSelectedEvent(event);

    const streetViewService = new google.maps.StreetViewService();
    streetViewService.getPanorama(
      {
        location: {
          lat: event.latLng?.lat() as number,
          lng: event.latLng?.lng() as number,
        },
        radius: 50,
      },
      (data, status) => {
        if (data && status === google.maps.StreetViewStatus.OK) {
          setIsStreetViewAvailable(true);
        } else {
          setIsStreetViewAvailable(false);
          streetView.setVisible(false);
        }
      },
    );
    streetView.setPosition({
      lat: event.latLng?.lat() as number,
      lng: event.latLng?.lng() as number,
    });
    setAnchorEl(event.domEvent.currentTarget as HTMLElement);
  };

  const toggleStreetView = () => streetView.setVisible(true);

  // Check if info is an array (hover/click dual content)
  const hasHoverClickContent = Array.isArray(info) && info.length === 2;

  const handleClickMarker = (e: google.maps.MapMouseEvent) => {
    e.stop();

    if (info) {
      setInteractionType('click');
      if (!showInfo) toggleInfo();
    } else if (showContextMenu) {
      handleMenuClick(e);
    } else if (onClick) {
      onClick(e);
    }
  };

  const handleMarkerMouseEnter = () => {
    const infoArray = info as ReactNode[];

    const isOffline: boolean =
      isValidElement(infoArray?.[1]) &&
      infoArray[1].props.timestamp &&
      differenceInMinutes(new Date(), new Date(infoArray[1].props.timestamp)) >
        5;

    if (hasHoverClickContent && isOffline) {
      setInteractionType('hover');
      if (!showInfo) toggleInfo();
    }
  };

  const handleMarkerMouseLeave = () => {
    // Only close on mouse leave if it was opened by hover
    if (hasHoverClickContent && interactionType === 'hover') {
      setInteractionType(null);
      if (showInfo) toggleInfo();
    }
  };

  const renderIcon = () => {
    if (typeof icon === 'string') {
      return (
        <img
          id="marker"
          src={icon}
          alt="Map marker"
          style={{ maxHeight: 25, filter: grayedOut ? 'grayscale(100%)' : '' }}
        />
      );
    }

    if (isDroppable) {
      const iconProps = (icon as React.ReactElement).props;
      const color = iconProps.color;
      return (
        <DroppableIcMarkerCfs
          color={color}
          isDroppable={isDroppable}
          cfsId={cfsId}
          cfsNumber={cfsNumber}
          address={address}
        />
      );
    }

    // Apply filter to the wrapper div for ReactNode icons
    return (
      <div id="marker" style={{ filter: grayedOut ? 'grayscale(100%)' : '' }}>
        {icon}
      </div>
    );
  };

  // Get the correct content based on interaction type
  const getInfoContent = () => {
    // If it's not an array, return info as-is
    if (!hasHoverClickContent) return info;

    const infoArray = info as ReactNode[];

    // Array format: [hoverContent, clickContent]
    if (interactionType === 'hover') {
      return infoArray[0]; // Show first item on hover
    } else if (interactionType === 'click') {
      return infoArray[1]; // Show second item on click
    }

    return info;
  };

  // Create standard marker with click handlers
  const markerElement = (
    <AdvancedMarker
      ref={markerRef}
      onClick={handleClickMarker}
      onMouseEnter={handleMarkerMouseEnter}
      onMouseLeave={handleMarkerMouseLeave}
      {...rest}
      zIndex={showInfo ? 1000 : undefined}
      data-marker-id={rest.key || 'marker'}
      clickable={true}
    >
      {renderIcon()}
      {selectedEvent && (
        <ContextMenu
          anchorEl={anchorEl}
          open={open}
          handleMenuClose={handleMenuClose}
          toggleStreetView={toggleStreetView}
          onClick={onClick}
          selectedEvent={selectedEvent}
          isStreetViewAvailable={isStreetViewAvailable}
        />
      )}
    </AdvancedMarker>
  );

  // Create info window if needed
  const infoWindowElement = info && showInfo && (
    <InfoWindow anchor={marker} maxWidth={maxWidth || 500} zIndex={1001}>
      {typeof info === 'string' &&
      typeof icon === 'string' &&
      icon.includes('fire_hydrant') &&
      properties ? (
        <FireHydrantPopup
          data={properties as IHydrantProperties}
          onClose={toggleInfo}
        />
      ) : infoWindowFullBg ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minWidth: '100%',
            boxSizing: 'border-box',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : '#fff',
            color: 'text.primary',
          }}
        >
          <IconButton
            onClick={toggleInfo}
            sx={{ position: 'absolute', right: '2px', top: '2px', padding: 0 }}
          >
            <IcClose style={{ width: 15, height: 15 }} />
          </IconButton>
          {getInfoContent()}
        </Box>
      ) : (
        <>
          <IconButton
            onClick={toggleInfo}
            sx={{ position: 'absolute', right: '2px', top: '2px', padding: 0 }}
          >
            <IcClose style={{ width: 15, height: 15 }} />
          </IconButton>
          {getInfoContent()}
        </>
      )}
    </InfoWindow>
  );

  // Return the marker and info window
  return (
    <>
      {markerElement}
      {infoWindowElement}
    </>
  );
};
